import { useEffect, useRef, useState } from 'react';
import { DDMenuItem, HandleEvent } from '../js/types';
import { checkSign, ResultObj } from '../js/Domain';
import {
    TableBody,
    TableCell,
    TableRow,
    TableHead,
    IconButton,
    TableContainer,
    Table,
    Paper,
    Box,
    Typography
} from '@material-ui/core';
import {
    Fullscreen,
    FullscreenExit,
    SignalWifiOff,
    SignalWifi4Bar,
    SignalWifi1Bar,
    SignalWifi2Bar
} from '@material-ui/icons';

import { makeStyles, Theme } from '@material-ui/core/styles';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import './progressBoard.css';
import { useWorkOrderApi } from '../js/adapter/WorkOrderAdapter';
import { autoThrowError } from '../js/function';
import { CustomDate } from '../utils/date/dateExtension';
import { DDMenu, OptionListType } from '../js/api';
import { ReactOutlineSelect } from '../_AAA/components/ReactTextField';
import { MaintainStatusType } from '../_AAA/control/CarRepair/MaintainStatus/consts';
import { useHistory, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';

//車輛維修狀態轉文字
const maintainStatusTextMap = new Map<string, string>([
    [MaintainStatusType.WAITING, "等待維修"],
    [MaintainStatusType.REPAIRING, "維修中"],
    [MaintainStatusType.WASHING, "洗車中"],
    [MaintainStatusType.COMPLETED, "完工待交"],
    [MaintainStatusType.STOP, "暫停"],
]);

const useStyles = makeStyles((theme: Theme) => ({
    boardContainer: {
        width: '100%',
        maxWidth: '1880px',
        [theme.breakpoints.down('lg')]: {
            maxWidth: '1400px',
        },
    },
    board: {
        backgroundColor: '#ffffff',
        borderRadius: 20,
        boxShadow: '0 8px 20px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden',
    },
    table: {
        overflow: 'hidden',
    },
    header: {
        backgroundColor: '#ffffff',
        color: '#3b5998',
        fontSize: '64px',
        fontWeight: 'bold',
        height: '160px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        [theme.breakpoints.down('lg')]: {
            fontSize: '48px',
            height: '120px',
        },
    },
    tableRowHead: {
        backgroundColor: '#5B6FAC',
        height: '120px',
        [theme.breakpoints.down('lg')]: {
            height: '100px',
        },
    },
    tableBody: {
        height: '575px',
        [theme.breakpoints.down('lg')]: {
            height: '475px',
        },
    },
    tableCellHead: {
        color: '#ffffff',
        fontSize: '48px',
        fontWeight: 'bold',
        textAlign: 'center',
        width: '25%',
        [theme.breakpoints.down('lg')]: {
            fontSize: '32px',
        },
    },
    tableRow: {
        height: '115px',
        border: '1px solid #AAB8C6',
        [theme.breakpoints.down('lg')]: {
            height: '95px',
        },
    },
    tableCell: {
        fontSize: '40px',
        fontWeight: 'bold',
        textAlign: 'center',
        width: '25%',
        [theme.breakpoints.down('lg')]: {
            fontSize: '32px',
        },
    },
    chip: {
        width: '220px',
        padding: '8px 0px',
        borderRadius: '50px',
        display: 'inline-block',
        fontSize: '40px',
        fontWeight: 'bold',
        lineHeight: '1.5',
        [theme.breakpoints.down('lg')]: {
            fontSize: '32px',
        },
    },
    chipWaiting: {
        color: '#000',
        backgroundColor: '#FFF',
    },
    chipRepairing: {
        color: '#ffffff',
        backgroundColor: '#F7941D',
    },
    chipDone: {
        color: '#ffffff',
        backgroundColor: '#009A66',
    },
    pageNumber: {
        fontSize: '32px',
        fontWeight: 'bold',
        marginRight: '12px'
    },
    positionSelector: {
        width: '120px',
        marginRight: '12px'
    },
    iconButton: {
        height: '48px',
        width: '48px',
    }
}));

interface ProgressBoardResItem {
    license_plate: string;
    time_enter: string;
    predict_delivery_datetime: string;
    maintain_status: string;
}

const ProgressBoard = () => {
    const history = useHistory();
    const location = useLocation();

    const workorderApiManager = useWorkOrderApi();

    const [boardData, setBoardData] = useState<ProgressBoardResItem[]>([])

    //據點代碼
    const [codeList_POS, setCodeList_POS] = useState<OptionListType[]>([]);
    //目前的據點
    const [currPOS, setCurrPOS] = useState<{ name: string; value: string; } | null>(null)

    //是否全熒幕
    const [isFullScreen, setIsFullScreen] = useState(false);

    const [totalPage, setTotalPage] = useState(1);
    const [currPage, setCurrPage] = useState(1);

    const classes = useStyles();
    const { closeSnackbar } = useSnackbar();

    const apiFailCountRef = useRef(0);

    // 網路連線狀態
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    // 用於判斷網路環境好壞
    const [responseSpeed, setResponseSpeed] = useState<number | null>(null);

    const getConnectIconView = () => {
        if (isOnline && responseSpeed !== null) {
            if (responseSpeed < 10) return <SignalWifi1Bar fontSize='large' color='error' />;
            else if (responseSpeed < 100) return <SignalWifi2Bar fontSize='large' style={{ color: '#ff9100' }} />;
            else return <SignalWifi4Bar fontSize='large' color='secondary' />;
        } else {
            return <SignalWifiOff fontSize='large' color='disabled' />
        }
    }
    //檢查登入狀態
    useEffect(() => {
        closeSnackbar();
        // 若 sessionStorage 無 company_code，代表尚未登入，不呼叫 checkSign
        // 否則 allFetch 無法取得 api_version，會跳出不必要的 alert
        // 未登入的跳轉由下方的 useEffect（依賴 history/location）負責處理
        if (!sessionStorage.getItem('company_code')) return;
        checkSign().then(loginRes => {
            if (!loginRes.success) {
                history.push(`/Login?redirect=${location.pathname}${location.search}`);
            }
        })
    }, [])

    useEffect(() => {
        const updateOnlineStatus = () => setIsOnline(navigator.onLine);

        window.addEventListener("online", updateOnlineStatus);
        window.addEventListener("offline", updateOnlineStatus);

        return () => {
            window.removeEventListener("online", updateOnlineStatus);
            window.removeEventListener("offline", updateOnlineStatus);
        };
    }, []);

    useEffect(() => {
        const handleDblClick = () => setIsFullScreen(prev => !prev);
        const handleFullscreenChange = () => {
            if (!document.fullscreenElement) setIsFullScreen(false);
        };
        document.addEventListener("dblclick", handleDblClick);
        document.addEventListener("fullscreenchange", handleFullscreenChange);
        return () => {
            document.removeEventListener("dblclick", handleDblClick);
            document.removeEventListener("fullscreenchange", handleFullscreenChange);
        };
    }, []);

    useEffect(() => {
        (async () => {
            //如果沒有登入，跳轉到登入頁面
            if (!sessionStorage.getItem('company_code')) {
                history.push(`/Login?redirect=${location.pathname}${location.search}`);
                return;
            }

            //獲取廠別選項
            const resPOS = await DDMenu.selectorcode<DDMenuItem>({ spp_id: "POS" });
            const resPOSList = resPOS.map(({ sps_id, name }) => ({ name, value: sps_id }))
            setCodeList_POS(resPOSList)

            //獲取網址上的POS_ID參數
            const query = new URLSearchParams(location.search);
            const queryPosID = query.get('POS_ID') || '';

            //優先使用query上的資訊，選項裏找不到就選取選項第一個
            const targetPos = resPOSList.find(item => item.value === queryPosID) || resPOSList[0];
            if (targetPos) {
                setCurrPOS(targetPos)
            } else {
                console.warn("無法取得據點清單，目前 currPOS 為空");
            }
        })()

    }, [history, location.pathname])

    useEffect(() => {
        if (!workorderApiManager || !currPOS) return;

        const getBoardData = async (pageNum: number) => {
            const startTime = performance.now();
            const todayDate = new CustomDate();
            const today = todayDate.toYYYYMMDD();
            const tomorrowDate = new CustomDate(new Date(todayDate.getFullYear(), todayDate.getMonth(), todayDate.getDate() + 1));
            const tomorrow = tomorrowDate.toYYYYMMDD();
            try {
                const { data: resData, page } = await autoThrowError(workorderApiManager.WorkOrderMasterSearch({
                    wom_code: "",
                    wom_category: "",
                    status: [],
                    user_id: "",
                    position_id: currPOS.value,
                    customer_id: "",
                    car_id: "",
                    license_plate: "",
                    time_enter_start: today,
                    time_enter_end: "",
                    time_leave_start: "",
                    time_leave_end: "",
                    insurance_company_id: "",
                    is_complete: "",
                    complete_time_start: "",
                    complete_time_end: "",
                    is_caseclose: "",
                    caseclose_time_start: "",
                    caseclose_time_end: "",
                    is_invalid: "",
                    invalid_time_start: "",
                    invalid_time_end: "",
                    invoice_number: "",
                    is_panelpaint: "N",
                    is_dashboard_visible: "Y",
                    excel: "",
                    predict_delivery_datetime_start: "",
                    predict_delivery_datetime_end: tomorrow,
                    maintain_statuses: [MaintainStatusType.WAITING, MaintainStatusType.REPAIRING, MaintainStatusType.WASHING, MaintainStatusType.COMPLETED],
                    page: pageNum,
                    num_per_page: 5
                }))
                const endTime = performance.now();

                setTotalPage(page || 1)

                //不夠5個要補齊資料,防止列表跑位
                const newBoardData: ProgressBoardResItem[] = []
                for (let i = 0; i < 5; i++) {
                    if (resData[i]) {
                        newBoardData.push(resData[i])
                    } else {
                        newBoardData.push({
                            license_plate: "",
                            time_enter: "",
                            predict_delivery_datetime: "",
                            maintain_status: "",
                        })
                    }
                }

                setBoardData(newBoardData)

                //計算下載速度,將下載的資料轉為JSON字串，並計算其大小和下載時間
                //沒法直接用回傳時間作爲網速依據，因爲字串太小了
                const text = JSON.stringify(resData);
                const durationSeconds = (endTime - startTime) / 1000;
                const bytes = new TextEncoder().encode(text).length;
                const speedKbps = (bytes / 1024) / durationSeconds;
                setResponseSpeed(speedKbps * 10000);
                apiFailCountRef.current = 0;

            } catch (error) {
                //出了問題,檢查一下是不是網路問題，如果不是網路問題，就不要繼續查詢,并且跳回登入頁面
                const errObj = error as ResultObj;
                // 「API未實現」屬於 Adapter 設定問題，非登入驗證失敗，不應跳轉登入頁面
                const isAdapterError = errObj.message?.includes('API未實現');
                if (navigator.onLine && !isAdapterError) {
                    apiFailCountRef.current += 1;
                    if (apiFailCountRef.current >= 6) {
                        clearInterval(getBoardDataInterval)
                        history.push(`/Login?redirect=${location.pathname}${location.search}&error=${errObj.message || '未知錯誤'}`);
                    }
                } else {
                    apiFailCountRef.current = 0;
                }
            }
        }

        //第一次進入時就馬上查詢一次資料
        getBoardData(currPage)

        const getBoardDataInterval = setInterval(() => {

            setCurrPage(prevPage => {
                if (prevPage + 1 > totalPage) {
                    getBoardData(1)
                    return 1;
                } else {
                    getBoardData(prevPage + 1)
                    return prevPage + 1
                }
            })

        }, 10000);

        //每6小時更新一次token
        const sixHours = 6 * 60 * 60 * 1000;
        const checkSignInterval = setInterval(() => {
            checkSign()
        }, sixHours);

        return () => {
            clearInterval(getBoardDataInterval)
            clearInterval(checkSignInterval);
        }
    }, [workorderApiManager, currPOS, totalPage, currPage]);

    useEffect(() => {
        if (isFullScreen) {
            document.documentElement.requestFullscreen().catch(err => { });
        } else {
            if (document.fullscreenElement) {
                document.exitFullscreen().catch(err => { });
            }
        }
    }, [isFullScreen])

    useEffect(() => {
        if (!currPOS) return;
        // 更新網址上的POS_ID參數（使用 history.replace 保持 React Router location 同步）
        const params = new URLSearchParams(location.search);
        params.set("POS_ID", currPOS.value);
        history.replace({ pathname: location.pathname, search: params.toString() });
    }, [currPOS])

    return (
        <>
            <Box className={classes.boardContainer}>
                <Box className={classes.board}>
                    <Typography className={classes.header}>車輛維修狀態看板</Typography>

                    <TableContainer component={Paper}>
                        <Table className={classes.table}>
                            <TableHead>
                                <TableRow className={classes.tableRowHead}>
                                    <TableCell className={classes.tableCellHead}>車牌號碼</TableCell>
                                    <TableCell className={classes.tableCellHead}>進廠時間</TableCell>
                                    <TableCell className={classes.tableCellHead}>預交時間</TableCell>
                                    <TableCell className={classes.tableCellHead}>車輛維修狀態</TableCell>
                                </TableRow>
                            </TableHead>
                            <SwitchTransition mode="out-in">
                                <CSSTransition
                                    key={JSON.stringify(boardData)}
                                    timeout={1000}
                                    classNames="table-fade"
                                >
                                    <TableBody className={classes.tableBody}>
                                        {boardData.map((elem, index) => {

                                            const maintainStatusText = maintainStatusTextMap.get(elem.maintain_status) || '';
                                            return (
                                                <TableRow
                                                    key={`${index}-${elem.license_plate}`}
                                                    className={`${classes.tableRow} fade-row`}
                                                    style={{ animationDelay: `${index * 200}ms` }}
                                                >
                                                    <TableCell className={classes.tableCell}>{elem.license_plate}</TableCell>
                                                    <TableCell className={classes.tableCell}>{elem.license_plate ? (elem.time_enter ? new CustomDate(elem.time_enter).toHHmm(":") : '-- : --') : ''}</TableCell>
                                                    <TableCell className={classes.tableCell}>{elem.license_plate ? (elem.predict_delivery_datetime ? new CustomDate(elem.predict_delivery_datetime).toHHmm(":") : '-- : --') : ''}</TableCell>
                                                    <TableCell className={classes.tableCell}>
                                                        {
                                                            maintainStatusText &&
                                                            <span
                                                                className={`${classes.chip} ${elem.maintain_status === MaintainStatusType.WAITING ? classes.chipWaiting : elem.maintain_status === MaintainStatusType.REPAIRING ? classes.chipRepairing : classes.chipDone}`}
                                                            >
                                                                {maintainStatusText}
                                                            </span>
                                                        }
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        }
                                        )}
                                    </TableBody>
                                </CSSTransition>
                            </SwitchTransition>

                        </Table>
                    </TableContainer>
                </Box>
                <Box display="flex" alignItems="center" justifyContent='flex-end' m={1}>
                    <Box marginRight={1}>
                        {getConnectIconView()}
                    </Box>
                    {
                        isFullScreen ?
                            <Typography className={classes.pageNumber} style={{ marginRight: '24px' }}>{currPOS?.name}</Typography>
                            :
                            <Box className={classes.positionSelector}>
                                <ReactOutlineSelect
                                    color="primary"
                                    id="search--POS"
                                    name="search--POS"
                                    placeholder="請選擇"
                                    optionKey="value"
                                    changeEvent={(e: HandleEvent) => {
                                        setCurrPOS(e.target.value)
                                        setCurrPage(1);
                                        setTotalPage(1);
                                    }}
                                    value={currPOS}
                                    codeList={codeList_POS}
                                    label="據點"
                                />
                            </Box>
                    }
                    <Typography onClick={() => {
                        setCurrPage(prev => {
                            if (prev + 1 > totalPage) {
                                return 1;
                            } else {
                                return prev + 1;
                            }
                        })
                    }} className={classes.pageNumber}>{`${currPage} / ${totalPage}`}</Typography>
                    <IconButton
                        className={classes.iconButton}
                        onClick={() => setIsFullScreen(prev => !prev)}
                    >
                        {isFullScreen ? <FullscreenExit fontSize='large' color='primary' /> : <Fullscreen fontSize='large' color='primary' />}
                    </IconButton>
                </Box>
            </Box>
        </>
    )
}

export default ProgressBoard;